import type moment from 'moment'
import {
  showFlexibleCancellation,
  showFreeCancellation,
} from 'lib/offer/cancellationPolicyUtils'
import {
  getOfferFlexibleCancellationReferenceDate,
  getOfferFlexibleCancellationTimelineContext,
  type OfferFlexibleCancellationTimelineContext,
} from './getOfferFlexibleCancellationTimelineContext'

export interface FlashCancellationPolicyView {
  policyReferenceDate: string | moment.Moment | undefined
  showFreeCancellation: boolean
  /**
   * Mount flexible USP/timeline only when defined.
   * Includes charter-resolving and expired-window hide rules.
   */
  flexibleTimelineContext: OfferFlexibleCancellationTimelineContext | undefined
  /**
   * Non-refundable label gate. Uses raw `showFlexibleCancellation` eligibility (not the
   * mountable context) so intentionally-hidden flexible states do not flash "Non-refundable".
   */
  showNonRefundable: boolean
}

interface GetFlashCancellationPolicyViewParams {
  cancellationPolicy?: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  charterDepartDate?: string | moment.Moment
  purchaseDate?: string
  isCharterModeEnabled: boolean
  isChangePackage?: boolean
  availableForPurchase?: boolean
}

/**
 * Single decision result for Flash cancellation surfaces (React + Trip Planner snapshot).
 * Callers should render from this view rather than re-deriving reference dates or eligibility.
 */
export function getFlashCancellationPolicyView(
  params: GetFlashCancellationPolicyViewParams,
): FlashCancellationPolicyView {
  const {
    cancellationPolicy,
    propertyTimezoneOffset,
    checkInDate,
    charterDepartDate,
    purchaseDate,
    isCharterModeEnabled,
    isChangePackage,
    availableForPurchase = true,
  } = params

  const referenceDate = getOfferFlexibleCancellationReferenceDate({
    cancellationPolicy,
    isCharterModeEnabled,
    checkInDate,
    charterDepartDate,
  })
  const policyReferenceDate = referenceDate.timelineReferenceDate

  const freeCancellation = showFreeCancellation(
    policyReferenceDate,
    isChangePackage,
    cancellationPolicy,
  )
  // Keep raw eligibility for the non-refundable gate. Mountable flexible UI may still be hidden
  // (charter departure resolving / expired window) without flipping to Non-refundable.
  const flexibleCancellationEligible = showFlexibleCancellation(
    policyReferenceDate,
    isChangePackage,
    cancellationPolicy,
  )

  // Skip context assembly when the raw gate already failed; pass referenceDate to avoid a second pass.
  const flexibleTimelineContext = (
    !isChangePackage &&
    !!cancellationPolicy &&
    flexibleCancellationEligible
  )
    ? getOfferFlexibleCancellationTimelineContext({
        cancellationPolicy,
        propertyTimezoneOffset,
        checkInDate,
        charterDepartDate,
        purchaseDate,
        isCharterModeEnabled,
        availableForPurchase,
        referenceDate,
      })
    : undefined

  return {
    policyReferenceDate,
    showFreeCancellation: freeCancellation,
    flexibleTimelineContext,
    // Change-package suppresses free/flexible display; leave blank rather than Non-refundable.
    // Missing policy must not render Non-refundable either.
    showNonRefundable: (
      !!cancellationPolicy &&
      !isChangePackage &&
      !freeCancellation &&
      !flexibleCancellationEligible &&
      cancellationPolicy.type !== 'hidden-cancellation-policy'
    ),
  }
}
